import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";
import { INSTALL } from "@/constants/testIds";

const DISMISSED = "pettok_install_dismissed";
const VISITS = "pettok_visits";

const isPhone = () => window.matchMedia("(max-width: 767px)").matches;
const isInstalled = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIosSafari = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);

// Counts one visit per browser session.
const countVisit = () => {
  if (sessionStorage.getItem(VISITS)) return Number(localStorage.getItem(VISITS) || 1);
  const n = Number(localStorage.getItem(VISITS) || 0) + 1;
  localStorage.setItem(VISITS, String(n));
  sessionStorage.setItem(VISITS, "1");
  return n;
};

const useInstallInvite = () => {
  const [deferred, setDeferred] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!isPhone() || isInstalled() || localStorage.getItem(DISMISSED)) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onAdded = () => setTimeout(() => setReady(true), 3000);  // after the "added" toast clears
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("pettok:added", onAdded);
    if (countVisit() >= 2) setReady(true);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("pettok:added", onAdded); };
  }, []);
  return { deferred, ready };
};

export const InstallPrompt = () => {
  const { deferred, ready } = useInstallInvite();
  const [closed, setClosed] = useState(false);
  const ios = isIosSafari();
  if (closed || !ready || !(deferred || ios)) return null;
  const dismiss = () => { localStorage.setItem(DISMISSED, "1"); setClosed(true); };
  const install = async () => {
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    dismiss();
  };
  return (
    <div data-testid={INSTALL.banner} role="dialog" aria-label="Add PetTok Shop to your home screen" className="md:hidden fixed z-[45] left-3 right-3 top-[4.75rem] bg-white rounded-2xl border border-[#F0EBE1] shadow-[0_12px_32px_rgba(28,25,23,0.14)] p-3 flex items-center gap-3 fade-up">
      <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-heading font-bold text-sm leading-tight">Keep Kiddee one tap away</p>
        {ios ? (
          <p data-testid={INSTALL.iosHelp} className="text-xs text-stone-500 leading-snug mt-0.5">
            Tap <Share size={12} className="inline -mt-0.5" aria-label="Share" /> then <strong>Add to Home Screen</strong>
          </p>
        ) : (
          <p className="text-xs text-stone-500 leading-snug mt-0.5">Add PetTok Shop to your home screen like an app.</p>
        )}
      </div>
      {!ios && (
        <button data-testid={INSTALL.add} onClick={install} className="shrink-0 h-10 px-4 rounded-full bg-[#E06D44] text-white text-sm font-semibold active:scale-95 transition-transform">Add</button>
      )}
      <button data-testid={INSTALL.dismiss} onClick={dismiss} aria-label="Not now" className="shrink-0 w-10 h-10 grid place-items-center rounded-full text-stone-400 hover:bg-[#F0EBE1]"><X size={18} /></button>
    </div>
  );
};
